import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    Sparkles, 
    Gift, 
    MessageSquare, 
    Clock, 
    Users, 
    Heart,
    ChevronRight,
    Check,
    Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BetaSignup = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user, token } = useAuth();
    const [programInfo, setProgramInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        how_did_you_hear: '',
        primary_use_case: '',
        has_adhd_diagnosis: null,
        // New strategic questions
        biggest_challenge: '',
        tried_other_apps: '',
        what_didnt_work: '',
        most_excited_feature: '',
        would_pay: '',
        ideal_price: '',
        use_frequency: '',
        additional_thoughts: ''
    });

    useEffect(() => {
        fetchProgramInfo();
    }, []);

    const fetchProgramInfo = async () => {
        try {
            const response = await fetch(`${API_URL}/api/beta-program`);
            if (response.ok) {
                const data = await response.json();
                setProgramInfo(data);
            }
        } catch (error) {
            console.error('Failed to fetch beta program info:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async () => {
        if (!isAuthenticated) {
            navigate('/auth', { state: { returnTo: '/beta' } });
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/api/beta-program/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                const data = await response.json();
                toast.success('Welcome to the beta!', { description: data.message });
                navigate('/home');
            } else {
                const error = await response.json();
                toast.error('Signup failed', { description: error.detail });
            }
        } catch (error) {
            console.error('Beta signup error:', error);
            toast.error('Something went wrong', { description: 'Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#1D6B6B]" />
            </div>
        );
    }

    if (!programInfo?.is_active) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex items-center justify-center px-4">
                <div className="text-center max-w-md">
                    <Heading as="h1" className="text-[#1D6B6B] mb-4">Beta Program Closed</Heading>
                    <BodyText className="text-[#7D8E95] mb-6">
                        Our beta program is no longer accepting new signups. Check out our pricing for full access!
                    </BodyText>
                    <PrimaryButton onClick={() => navigate('/pricing')}>
                        View Pricing
                    </PrimaryButton>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4]">
            <Helmet>
                <title>Try CoRegulate Free - 30 Day Beta Access | ADHD Emotional Regulation</title>
                <meta name="description" content="Get 30 days of free access to CoRegulate's premium features. Help shape the future of ADHD-friendly emotional regulation tools." />
            </Helmet>

            {/* Hero Section */}
            <section className="px-4 pt-12 pb-8 max-w-4xl mx-auto text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6B6B]/10 text-[#1D6B6B] text-sm font-medium mb-6">
                        <Sparkles className="w-4 h-4" />
                        <span>Pre-Launch Beta</span>
                    </div>

                    <Heading as="h1" size="text-4xl sm:text-5xl" className="text-[#1D6B6B] mb-4">
                        Try CoRegulate Free<br />
                        <span className="text-[#FFBB98]">for 30 Days</span>
                    </Heading>

                    <BodyText className="text-[#7D8E95] text-lg max-w-2xl mx-auto mb-8">
                        Help us build the best emotional regulation app for ADHD minds. 
                        Get full premium access — all we ask is your honest feedback.
                    </BodyText>
                </motion.div>

                {/* What You Get */}
                <div className="grid md:grid-cols-3 gap-4 mb-12">
                    {[
                        { icon: Gift, title: '30 Days Free', description: 'Full Support+ tier access with all premium features' },
                        { icon: MessageSquare, title: 'Bonus Days', description: 'Earn up to 21 extra days by sharing feedback' },
                        { icon: Heart, title: 'Shape the Future', description: 'Your input directly influences what we build' }
                    ].map((item, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="glass-card p-6"
                        >
                            <div className="w-12 h-12 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-4">
                                <item.icon className="w-6 h-6 text-[#1D6B6B]" />
                            </div>
                            <Heading as="h3" size="text-lg" className="text-[#1D6B6B] mb-2">
                                {item.title}
                            </Heading>
                            <SmallText className="text-[#7D8E95]">{item.description}</SmallText>
                        </motion.div>
                    ))}
                </div>

                {/* What's Included */}
                <div className="glass-card p-8 mb-12 text-left max-w-2xl mx-auto">
                    <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-6 text-center">
                        Everything in Support+ Tier
                    </Heading>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {[
                            'AI Guide "Sage" — your ADHD-aware companion',
                            'Guided meditations (RSD, Anxiety, Focus)',
                            'Breathing & grounding exercises',
                            'Relationship tools & Infinity Loop',
                            'EMDR/Bilateral stimulation',
                            'Body scan & PMR',
                            'Partner mode for couples',
                            'Daily check-ins & tracking'
                        ].map((feature, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-[#1D6B6B] flex-shrink-0" />
                                <SmallText className="text-[#5A6A6A]">{feature}</SmallText>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Strategic Feedback Questions */}
                <div className="glass-card p-8 mb-8 text-left max-w-xl mx-auto">
                    <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-2 text-center">
                        Help Us Build What You Need
                    </Heading>
                    <SmallText className="text-[#7D8E95] mb-6 block text-center">
                        Your answers help us prioritize features that matter most
                    </SmallText>
                    
                    <div className="space-y-6">
                        {/* How did you hear */}
                        <div>
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                How did you hear about CoRegulate?
                            </SmallText>
                            <select
                                value={formData.how_did_you_hear}
                                onChange={(e) => setFormData({...formData, how_did_you_hear: e.target.value})}
                                className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                         focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] outline-none"
                            >
                                <option value="">Select...</option>
                                <option value="social_media">Social Media (Twitter, Instagram, TikTok)</option>
                                <option value="reddit">Reddit</option>
                                <option value="friend">Friend or Family</option>
                                <option value="therapist">Therapist or Doctor</option>
                                <option value="search">Google Search</option>
                                <option value="adhd_community">ADHD Community/Forum</option>
                                <option value="podcast">Podcast</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Primary use case */}
                        <div>
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                What's your primary focus?
                            </SmallText>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { value: 'individual', label: 'My own regulation' },
                                    { value: 'relationship', label: 'Relationship support' },
                                    { value: 'both', label: 'Both' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setFormData({...formData, primary_use_case: opt.value})}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                            formData.primary_use_case === opt.value
                                                ? 'bg-[#1D6B6B] text-white'
                                                : 'bg-white/60 text-[#5A6A6A] hover:bg-white'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Biggest challenge - KEY INSIGHT */}
                        <div>
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                What's your biggest emotional regulation challenge? *
                            </SmallText>
                            <select
                                value={formData.biggest_challenge}
                                onChange={(e) => setFormData({...formData, biggest_challenge: e.target.value})}
                                className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                         focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] outline-none"
                            >
                                <option value="">Select your biggest challenge...</option>
                                <option value="rsd">RSD — Rejection sensitivity that hits hard</option>
                                <option value="emotional_flooding">Emotional flooding — Going 0-100 instantly</option>
                                <option value="anxiety">Anxiety and racing thoughts</option>
                                <option value="anger">Anger/frustration regulation</option>
                                <option value="shutdown">Shutting down/withdrawing</option>
                                <option value="relationship_conflicts">Conflict in relationships</option>
                                <option value="overwhelm">General overwhelm</option>
                                <option value="multiple">Several of these equally</option>
                            </select>
                        </div>

                        {/* Previous app experience */}
                        <div>
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                Have you tried other mental health/meditation apps?
                            </SmallText>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { value: 'yes_many', label: 'Yes, several' },
                                    { value: 'yes_few', label: 'Yes, 1-2' },
                                    { value: 'no', label: 'No' },
                                    { value: 'currently_using', label: 'Currently using one' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setFormData({...formData, tried_other_apps: opt.value})}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                            formData.tried_other_apps === opt.value
                                                ? 'bg-[#1D6B6B] text-white'
                                                : 'bg-white/60 text-[#5A6A6A] hover:bg-white'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* What didn't work - CRITICAL INSIGHT */}
                        {(formData.tried_other_apps === 'yes_many' || formData.tried_other_apps === 'yes_few') && (
                            <div>
                                <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                    What didn't work about them?
                                </SmallText>
                                <textarea
                                    value={formData.what_didnt_work}
                                    onChange={(e) => setFormData({...formData, what_didnt_work: e.target.value})}
                                    placeholder="e.g., Too slow, not ADHD-specific, couldn't stick with it..."
                                    rows={2}
                                    className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                             focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] outline-none resize-none"
                                />
                            </div>
                        )}

                        {/* Most excited feature - PRIORITIZATION */}
                        <div>
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                Which feature are you most excited to try?
                            </SmallText>
                            <select
                                value={formData.most_excited_feature}
                                onChange={(e) => setFormData({...formData, most_excited_feature: e.target.value})}
                                className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                         focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] outline-none"
                            >
                                <option value="">Select...</option>
                                <option value="ai_guide">AI Guide (Sage) — ADHD-aware support</option>
                                <option value="quick_tools">Quick regulation tools (60-sec exercises)</option>
                                <option value="meditations">Guided meditations (RSD, Anxiety)</option>
                                <option value="relationship_tools">Relationship tools & Infinity Loop</option>
                                <option value="partner_mode">Partner mode for couples</option>
                                <option value="tracking">Check-ins and tracking patterns</option>
                                <option value="all">Honestly, all of it</option>
                            </select>
                        </div>

                        {/* Usage frequency expectation */}
                        <div>
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                How often do you think you'd use this?
                            </SmallText>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { value: 'daily', label: 'Daily' },
                                    { value: 'few_times_week', label: 'Few times a week' },
                                    { value: 'when_needed', label: 'Only when I need it' },
                                    { value: 'unsure', label: 'Not sure yet' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setFormData({...formData, use_frequency: opt.value})}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                            formData.use_frequency === opt.value
                                                ? 'bg-[#1D6B6B] text-white'
                                                : 'bg-white/60 text-[#5A6A6A] hover:bg-white'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Willingness to pay - MONETIZATION */}
                        <div className="pt-4 border-t border-[#E8E4DC]">
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                If CoRegulate helps, would you consider paying for it?
                            </SmallText>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { value: 'yes', label: 'Yes' },
                                    { value: 'maybe', label: 'Maybe' },
                                    { value: 'no', label: 'Probably not' }
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setFormData({...formData, would_pay: opt.value})}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                            formData.would_pay === opt.value
                                                ? 'bg-[#1D6B6B] text-white'
                                                : 'bg-white/60 text-[#5A6A6A] hover:bg-white'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Price sensitivity - shown only if willing to pay */}
                        {(formData.would_pay === 'yes' || formData.would_pay === 'maybe') && (
                            <div>
                                <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                    What feels like a fair monthly price?
                                </SmallText>
                                <div className="grid grid-cols-4 gap-2">
                                    {[
                                        { value: 'under_10', label: 'Under $10' },
                                        { value: '10_15', label: '$10-15' },
                                        { value: '15_25', label: '$15-25' },
                                        { value: '25_plus', label: '$25+' }
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setFormData({...formData, ideal_price: opt.value})}
                                            className={`px-2 py-2 rounded-xl text-sm font-medium transition-all ${
                                                formData.ideal_price === opt.value
                                                    ? 'bg-[#1D6B6B] text-white'
                                                    : 'bg-white/60 text-[#5A6A6A] hover:bg-white'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Open-ended */}
                        <div>
                            <SmallText className="text-[#5A6A6A] font-medium mb-2 block">
                                Anything else you want us to know? (Optional)
                            </SmallText>
                            <textarea
                                value={formData.additional_thoughts}
                                onChange={(e) => setFormData({...formData, additional_thoughts: e.target.value})}
                                placeholder="What would make this app a must-have for you?"
                                rows={2}
                                className="w-full px-4 py-3 rounded-xl bg-white/80 border border-[#E8E4DC] 
                                         focus:border-[#1D6B6B] focus:ring-1 focus:ring-[#1D6B6B] outline-none resize-none"
                            />
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="max-w-md mx-auto">
                    <PrimaryButton
                        onClick={handleSignup}
                        disabled={submitting}
                        className="w-full text-lg py-4"
                        data-testid="beta-signup-btn"
                    >
                        {submitting ? (
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                            <Sparkles className="w-5 h-5 mr-2" />
                        )}
                        {isAuthenticated ? 'Start My Free 30 Days' : 'Create Account & Start Free'}
                    </PrimaryButton>
                    
                    <SmallText className="text-[#7D8E95] mt-4 block text-center">
                        No credit card required • Cancel anytime
                    </SmallText>
                </div>
            </section>

            {/* FAQ */}
            <section className="px-4 py-12 bg-white/30">
                <div className="max-w-2xl mx-auto">
                    <Heading as="h2" size="text-2xl" className="text-[#1D6B6B] mb-8 text-center">
                        Common Questions
                    </Heading>
                    
                    <div className="space-y-4">
                        {[
                            {
                                q: "What happens after 30 days?",
                                a: "You can choose to subscribe to continue with full access, or use the free tier with limited features. No automatic charges."
                            },
                            {
                                q: "How do I give feedback?",
                                a: "There's a gentle feedback button in the app that appears occasionally. It's never pushy — share thoughts when you're ready."
                            },
                            {
                                q: "What kind of feedback helps?",
                                a: "Everything! What tools work, what's confusing, what you wish existed, bugs you find. Honest feedback shapes what we build."
                            },
                            {
                                q: "Is my data private?",
                                a: "Absolutely. Your personal regulation data stays private. Feedback is used only to improve the app."
                            }
                        ].map((faq, i) => (
                            <div key={i} className="glass-card p-5">
                                <Heading as="h3" size="text-base" className="text-[#1D6B6B] mb-2">
                                    {faq.q}
                                </Heading>
                                <BodyText className="text-[#5A6A6A] text-sm">{faq.a}</BodyText>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BetaSignup;
